'use strict';

import { fetchLiveLayout } from './trialLiveLayoutApi';

function csvCell(value) {
  const text = value == null ? '' : String(value);
  const guarded = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
  if (/[",\n\r]/.test(guarded)) return `"${guarded.replace(/"/g, '""')}"`;
  return guarded;
}

function cellNum(value) {
  return typeof value === 'number' && Number.isFinite(value) ? String(value) : '';
}

/** One row per frame. Joint columns stay on this file; device samples are a second CSV. */
export function poseLayoutToCsv(layout) {
  const names = layout.meta.jointNames;
  const jointCount = names.length;
  const pose = layout.pose;
  const fps = layout.meta.fps;
  const header = ['frame', 'time_sec'];
  for (let j = 0; j < jointCount; j += 1) {
    header.push(`${names[j]}_x`, `${names[j]}_y`, `${names[j]}_z`);
  }
  const lines = [header.map(csvCell).join(',')];
  for (let frame = 0; frame < layout.meta.frameCount; frame += 1) {
    const row = [String(frame), cellNum(frame / fps)];
    for (let j = 0; j < jointCount; j += 1) {
      const o = (frame * jointCount + j) * 3;
      row.push(cellNum(pose[o]), cellNum(pose[o + 1]), cellNum(pose[o + 2]));
    }
    lines.push(row.join(','));
  }
  return `${lines.join('\n')}\n`;
}

const DEVICE_COLUMNS = ['t_ms', 'accel_x', 'accel_y', 'accel_z', 'gyro_x', 'gyro_y', 'gyro_z'];

/**
 * Device body is `{ devices: [{ name, samples: [{ t_ms, accel_*, gyro_* }] }] }`.
 * It is not merged onto the pose time column.
 */
export function deviceRawToCsv(data) {
  if (!data || data.status === 'failure' || !Array.isArray(data.devices)) {
    throw new Error((data && data.reason) || 'device raw failed');
  }
  const lines = [['device', ...DEVICE_COLUMNS].join(',')];
  for (let i = 0; i < data.devices.length; i += 1) {
    const device = data.devices[i];
    const samples = Array.isArray(device.samples) ? device.samples : [];
    for (let s = 0; s < samples.length; s += 1) {
      const sample = samples[s];
      const row = [csvCell(device.name)];
      for (let c = 0; c < DEVICE_COLUMNS.length; c += 1) {
        row.push(cellNum(sample[DEVICE_COLUMNS[c]]));
      }
      lines.push(row.join(','));
    }
  }
  return `${lines.join('\n')}\n`;
}

function saveCsvFile(csv, filename) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

async function fetchDeviceCsv(trialId, token) {
  const url = `${window.SERVER_URL}/a/get-evowalk-raw?id=${encodeURIComponent(trialId)}`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`device raw HTTP ${res.status}`);
  return deviceRawToCsv(await res.json());
}

/**
 * Saves `trial-<id>-pose.csv` and `trial-<id>-device.csv`.
 * live-pose is a float32 buffer; frame count and joint names come from fetchLiveLayout.
 * A failed file is reported after the other file has been saved.
 */
export async function downloadTrialPoseAndDeviceCsv(trialId) {
  if (trialId == null || String(trialId).trim() === '') {
    throw new Error('Missing trial id');
  }
  const token = localStorage.getItem('token');
  if (!token) throw new Error('Missing token');

  const id = String(trialId).trim();
  if (!/^[0-9A-Za-z_-]+$/.test(id)) {
    throw new Error('Invalid trial id');
  }
  const errors = [];

  try {
    const layout = await fetchLiveLayout(id);
    saveCsvFile(poseLayoutToCsv(layout), `trial-${id}-pose.csv`);
  } catch (err) {
    errors.push(`Pose: ${err.message}`);
  }

  try {
    saveCsvFile(await fetchDeviceCsv(id, token), `trial-${id}-device.csv`);
  } catch (err) {
    errors.push(`Device: ${err.message}`);
  }

  if (errors.length > 0) {
    throw new Error(errors.join('; '));
  }
}
